import styled, { css } from 'styled-components'
import { AnalyticsEvent } from 'analytics/performance'
import { isSameHexColor } from 'utils/color'
import { trueWhite } from 'theme/colors'
import { SvgIconType } from 'components/styled/icon/svg'
import { Box } from 'components/styled/containers/box'
import { SubtitleStyle } from '../typography/styles'

const ButtonVariants = {
  PRIMARY: 'primary',
  BASIC: 'basic',
  SECONDARY: 'secondary',
  BASIC_SECONDARY: 'basicSecondary',
  PRIMARY_GHOST: 'primaryGhost',
  SECONDARY_GHOST: 'secondaryGhost',
  TERTIARY: 'tertiary',
  TERTIARY_GHOST: 'tertiaryGhost',
} as const

export type ButtonVariant = typeof ButtonVariants[keyof typeof ButtonVariants]

export interface BaseButtonProps {
  variant?: ButtonVariant
  size?: 'small' | 'default'
  icon?: string
  iconRight?: string
  useOutlinedIcon?: boolean
  useOutlinedRightIcon?: boolean
  svgIcon?: SvgIconType
  text?: string
  label?: string
  loading?: boolean
  event?: AnalyticsEvent
  useConfirm?: boolean
}

export interface BaseButtonStyleProps {
  $variant: ButtonVariant
  $hasText: boolean
  $loading: boolean
  $size: 'small' | 'default'
}

export const ButtonBox = styled(Box).attrs({
  p: 's2',
})`
  max-width: 100vw !important;
`

export const ButtonText = styled.span<{ hasIcon: boolean; $loading?: boolean }>`
  ${SubtitleStyle};
  line-height: 2.4rem;
  padding: ${({ theme, hasIcon, $loading }) =>
    hasIcon || $loading ? `0 ${theme.space.s12}` : 0};
`

export const PrimaryFocusStyle = css`
  &.focus-visible {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.color.white},
      0 0 0 3px ${({ theme }) => theme.color.primary70};
  }
`

export const GreyFocusStyle = css`
  &.focus-visible {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.color.white},
      0 0 0 3px ${({ theme }) => theme.color.grey70};
  }
`
export const PrimaryStyle = css`
  background-color: ${({ theme }) => theme.color.primary110};

  && {
    color: ${({ theme }) => theme.color.primaryButtonText};
  }

  svg {
    fill: ${({ theme }) => theme.color.white};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.primary120};
    box-shadow: ${({ theme }) => theme.shadow.primaryHover};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.primary130};
    box-shadow: ${({ theme }) => theme.shadow.primaryHover};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.color.primary30};
    box-shadow: none;
  }

  ${PrimaryFocusStyle};
`

const BasicStyle = css`
  && {
    color: ${({ theme }) => theme.color.primary110};
  }

  svg {
    fill: ${({ theme }) => theme.color.primary110};
  }

  &:hover {
    color: ${({ theme }) => theme.color.primary120};
    background-color: ${({ theme }) => theme.color.primary05};

    svg {
      fill: ${({ theme }) => theme.color.primary120};
    }
  }

  &:active {
    color: ${({ theme }) => theme.color.primary120};
    background-color: ${({ theme }) => theme.color.primary10};

    svg {
      fill: ${({ theme }) => theme.color.primary120};
    }
  }

  &:disabled {
    background-color: transparent;
    color: ${({ theme }) => theme.color.primary30};

    svg {
      fill: ${({ theme }) => theme.color.primary30};
    }
  }

  ${PrimaryFocusStyle};
`

export const SecondaryStyle = css`
  background-color: ${({ theme }) => theme.color.grey07};

  && {
    color: ${({ theme }) => theme.color.bodyText};
  }

  svg {
    fill: ${({ theme }) => theme.color.bodyText};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.grey10};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.grey20};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.color.grey05};
    color: ${({ theme }) => theme.color.grey30};

    svg {
      fill: ${({ theme }) => theme.color.grey30};
    }
  }

  ${GreyFocusStyle};
`

export const BasicSecondaryStyle = css`
  && {
    color: ${({ theme }) => theme.color.grey90};
  }

  svg {
    fill: ${({ theme }) => theme.color.grey90};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.grey07};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.grey10};
  }

  &:disabled {
    background-color: transparent;
    color: ${({ theme }) => theme.color.grey30};

    svg {
      fill: ${({ theme }) => theme.color.grey30};
    }
  }

  ${GreyFocusStyle};
`

const PrimaryGhostStyle = css`
  border-color: ${({ theme }) => theme.color.primary110};

  && {
    color: ${({ theme }) => theme.color.primary110};
  }

  svg {
    fill: ${({ theme }) => theme.color.primary110};
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.primary120};
    color: ${({ theme }) => theme.color.primary120};

    svg {
      fill: ${({ theme }) => theme.color.primary120};
    }
  }

  &:active {
    border-color: ${({ theme }) => theme.color.primary130};
    color: ${({ theme }) => theme.color.primary130};

    svg {
      fill: ${({ theme }) => theme.color.primary130};
    }
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.primary30};
    color: ${({ theme }) => theme.color.primary30};

    svg {
      fill: ${({ theme }) => theme.color.primary30};
    }
  }

  ${PrimaryFocusStyle};
`

const SecondaryGhostStyle = css`
  border-color: ${({ theme }) => theme.color.grey20};

  && {
    color: ${({ theme }) => theme.color.bodyText};
  }

  svg {
    fill: ${({ theme }) => theme.color.bodyText};
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.grey30};
  }

  &:active {
    border-color: ${({ theme }) => theme.color.grey40};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.grey05};
    color: ${({ theme }) => theme.color.grey30};

    svg {
      fill: ${({ theme }) => theme.color.grey30};
    }
  }

  ${GreyFocusStyle};
`

/*
This style is not in use if client has selected some custom background color.
Tertiary button will have secondary button style if background color is not white.
*/
const TertiaryStyle = css`
  background-color: ${({ theme }) => theme.color.white};

  && {
    color: ${({ theme }) => theme.color.bodyText};
  }

  svg {
    fill: ${({ theme }) => theme.color.bodyText};
  }

  &:hover {
    color: ${({ theme }) => theme.color.primary110};
    box-shadow: ${({ theme }) => theme.shadow.tertiaryHover};

    svg {
      fill: ${({ theme }) => theme.color.primary110};
    }
  }

  &:active {
    color: ${({ theme }) => theme.color.primary120};
    box-shadow: ${({ theme }) => theme.shadow.tertiaryHover};

    svg {
      fill: ${({ theme }) => theme.color.primary120};
    }
  }

  &:disabled {
    color: ${({ theme }) => theme.color.grey30};
    box-shadow: none;

    svg {
      fill: ${({ theme }) => theme.color.grey30};
    }
  }

  ${PrimaryFocusStyle};
`

const TertiaryGhostStyle = css`
  border-color: ${({ theme }) => theme.color.white};

  && {
    color: ${({ theme }) => theme.color.white};
  }

  svg {
    fill: ${({ theme }) => theme.color.white};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.blackO30};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.blackO50};
  }

  &:disabled {
    background-color: transparent;
    border-color: ${({ theme }) => theme.color.grey30};
    color: ${({ theme }) => theme.color.grey30};

    svg {
      fill: ${({ theme }) => theme.color.grey30};
    }
  }

  &.focus-visible {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.color.grey60},
      0 0 0 3px ${({ theme }) => theme.color.white};
  }
`

const SmallButtonStyle = css<{ $hasText: boolean; $loading: boolean }>`
  padding: ${({ theme, $loading }) => ($loading ? 0 : theme.space.s4)}
    ${({ theme, $hasText }) => ($hasText ? theme.space.s8 : theme.space.s4)};
`

const DefaultButtonStyle = css<{ $hasText: boolean; $loading: boolean }>`
  padding: ${({ theme, $loading }) => ($loading ? 0 : theme.space.s8)}
    ${({ theme, $hasText }) => ($hasText ? theme.space.s16 : theme.space.s8)};
`

export const BaseButtonStyle = css<{
  $variant: ButtonVariant
  $hasText: boolean
  $loading: boolean
  $size: 'small' | 'default'
}>`
  max-width: 100%;
  border: 2px solid transparent;
  background-color: transparent;
  border-radius: ${({ theme }) => theme.space.s24};

  ${({ $size }) =>
    $size === 'default' ? DefaultButtonStyle : SmallButtonStyle};

  user-select: none;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-width: ${({ theme, $hasText }) => $hasText && theme.space.s80};

  outline: none;

  && {
    text-decoration: none;
  }

  ${({ $variant }) => $variant === ButtonVariants.PRIMARY && PrimaryStyle};
  ${({ $variant }) => $variant === ButtonVariants.BASIC && BasicStyle};
  ${({ $variant }) => $variant === ButtonVariants.SECONDARY && SecondaryStyle};
  ${({ $variant }) =>
    $variant === ButtonVariants.BASIC_SECONDARY && BasicSecondaryStyle};
  ${({ $variant }) =>
    $variant === ButtonVariants.PRIMARY_GHOST && PrimaryGhostStyle};
  ${({ $variant }) =>
    $variant === ButtonVariants.SECONDARY_GHOST && SecondaryGhostStyle};
  /* Tertiary button will have secondary button style if background color is not white */
  ${({ $variant, theme }) => {
    if ($variant === ButtonVariants.TERTIARY) {
      return isSameHexColor(theme.color.background, trueWhite)
        ? TertiaryStyle
        : SecondaryStyle
    }
    return false
  }};
  ${({ $variant }) =>
    $variant === ButtonVariants.TERTIARY_GHOST && TertiaryGhostStyle};
`
